import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

// Imported assets
import CheckmarkIcon from "../../../../assets/icons/CheckMark_Green.svg";
import CloseIcon from "../../../../assets/icons/Close_Red.svg";
// import DefaultAvatar from "../../../../assets/icons/ProfilePicForNavBar.svg";
import EditIcon from "../../../../assets/icons/Edit_WhiteIcon.svg";
import HideIcon from "../../../../assets/icons/Hide_BlackIcon.svg";
import UnhideIcon from "../../../../assets/icons/Un-Hide_BlackIcon.svg";

// // TODO: Remove temp avatars for demos
// import lgAvatar from "../../../assets/temp/lg-demo-avatar.png";
// import tonyWaltersAvatar from "../../../assets/temp/tony-k-walters.png";

// Imported components
import Info from "../../../../components/form/Info";
import StandardLabel from "../../../../components/form/Label";
import PrimaryButton from "../../../../components/ui/PrimaryButton";

// Imported contexts
import { useUserContext } from "../../../../contexts/User";

// Styled components
// const AvatarContainer = styled.div`
//   border-radius: 50%;
// `;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  justify-content: center;

  & > button {
    height: 40px;
    min-width: 160px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  padding: 40px;
  width: 100%;
`;

const ErrorInfo = styled(Info)`
  color: #d32f2f;
`;

const FormField = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: flex-start;
  position: relative;

  & .MuiInputBase-root {
    background: #ffffff;
    height: 40px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  // margin: 20px 0 0 20px;
`;

const HeaderButton = styled(PrimaryButton)`
  display: flex;
  flex-direction: row;
  font-family: Inter, Oxygen, Roboto, sans-serif;
  font-size: 14px;
  height: 32px;
  min-width: 140px;
  width: auto;
`;

const HeaderText = styled.span`
  align-content: center;
  font-family: Oxygen, Inter, Roboto, sans-serif;
  font-size: 36px;
  text-align: center;
`;

const Icon = styled.img`
  height: auto;
  margin-right: 10px;
  width: 20px;

  &:hover {
    cursor: pointer;
  }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 20px 20px;
`;

const Label = styled(StandardLabel)`
  font-family: Inter, Oxygen, Roboto, sans-serif;
  font-size: 16px;
  font-weight: 400;
`;

const PasswordRequirementsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 20px 0;
`;

const PasswordRequirementsHeader = styled.p`
  color: rgba(0, 0, 0, 0.5);
`;

const PasswordRequirementsText = styled.p`
  align-items: center;
  color: rgba(0, 0, 0, 1);
  display: flex;
  flex-direction: row;
`;

const PasswordSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  width: 100%;

  & * {
    font-family: Inter, Oxygen, Roboto, sans-serif !important;
  }
`;

const ResetButton = styled(HeaderButton)`
  background: white;
  border: 1px solid #d9d9d9;
  color: black;
  font-family: Inter, Oxygen, Roboto, sans-serif;

  &:hover {
    background: lab(from white calc(l - 5) a b / 100);
  }
`;

const RequirementsIcon = styled(Icon)`
  cursor: default;

  &:hover {
    cursor: default;
  }
`;

const ThemeButton = styled(HeaderButton)`
  background: var(--terbine-blue);
  font-family: Inter, Oxygen, Roboto, sans-serif;

  &:disabled {
    cursor: not-allowed;
  }

  &:hover {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 100);
  }
`;

const ChangePasswordButton = styled(ThemeButton)`
  font-family: Inter, Oxygen, Roboto, sans-serif;
  width: 240px;
`;

// const UserAvatar = styled.img`
//   height: auto;
//   width: 80px;
// `;

const SecuritySettings = () => {
  const {
    changePassword,
    user,
    /*passwordMax, */ passwordMin,
    validateInputs,
  } = useUserContext();

  const [currPassword, setCurrPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  // const [currPasswordErr, setCurrPasswordErr] = useState(null);
  // const [newPasswordErr, setNewPasswordErr] = useState(null);
  // const [confirmNewPasswordErr, setConfirmNewPasswordErr] = useState(null);

  const [changePasswordErrs, setChangePasswordErrs] = useState(null);

  const [showCurrPassword, setShowCurrPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const [inEditPasswordMode, setInEditPasswordMode] = useState(false);

  useEffect(() => {
    setChangePasswordErrs(
      validateInputs("ChangePassword", {
        oldPassword: currPassword,
        newPassword: newPassword,
        confirmPassword: confirmNewPassword,
      })
    );
  }, [currPassword, newPassword, confirmNewPassword, setChangePasswordErrs]);

  const toggleCurrPassword = useCallback(() => {
    setShowCurrPassword(!showCurrPassword);
  }, [setShowCurrPassword, showCurrPassword]);

  const toggleNewPassword = useCallback(() => {
    setShowNewPassword(!showNewPassword);
  }, [setShowNewPassword, showNewPassword]);

  const toggleConfirmNewPassword = useCallback(() => {
    setShowConfirmNewPassword(!showConfirmNewPassword);
  }, [setShowConfirmNewPassword, showConfirmNewPassword]);

  const resetForm = (e) => {
    e?.preventDefault();

    setCurrPassword("");
    setNewPassword("");
    setConfirmNewPassword("");

    // setCurrPasswordErr(null);
    // setNewPasswordErr(null);
    // setConfirmNewPasswordErr(null);

    setChangePasswordErrs(null);

    setShowCurrPassword(false);
    setShowNewPassword(false);
    setShowConfirmNewPassword(false);
  };

  return (
    <Container>
      <Header>
        <HeaderText>Security Settings</HeaderText>
      </Header>

      <InfoSection>
        {!inEditPasswordMode && (
          <ChangePasswordButton
            onClick={() => setInEditPasswordMode(true)}
            type="button"
          >
            <Icon src={EditIcon} /> Change Password
          </ChangePasswordButton>
        )}
        {inEditPasswordMode && (
          <>
            <PasswordSection>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  gap: "10px",
                }}
              >
                <FormField>
                  <FormControl sx={{ width: "100%" }}>
                    <Label>Current Password</Label>
                    <TextField
                      // error={currPasswordErr ? true : false}
                      onChange={(e) => setCurrPassword(e.target.value)}
                      slotProps={{
                        htmlInput: {
                          // maxLength: passwordMax,
                          minLength: passwordMin,
                        },
                        input: {
                          endAdornment: (
                            <Icon
                              onClick={() => toggleCurrPassword()}
                              src={showCurrPassword ? UnhideIcon : HideIcon}
                            />
                          ),
                        },
                      }}
                      type={showCurrPassword ? "text" : "password"}
                      value={currPassword}
                      variant="outlined"
                    />
                  </FormControl>
                </FormField>
                <FormField style={{ flex: 1 }}>
                  <FormControl sx={{ width: "100%" }}>
                    <Label
                      $hasError={Boolean(changePasswordErrs.oldAndNewMatchErr)}
                    >
                      New Password
                    </Label>
                    <TextField
                      error={Boolean(changePasswordErrs.oldAndNewMatchErr)}
                      onChange={(e) => setNewPassword(e.target.value)}
                      slotProps={{
                        htmlInput: {
                          // maxLength: passwordMax,
                          minLength: passwordMin,
                        },
                        input: {
                          endAdornment: (
                            <Icon
                              onClick={() => toggleNewPassword()}
                              src={showNewPassword ? UnhideIcon : HideIcon}
                            />
                          ),
                        },
                      }}
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      variant="outlined"
                    />
                    {Boolean(changePasswordErrs.oldAndNewMatchErr) && (
                      <ErrorInfo>
                        {changePasswordErrs.oldAndNewMatchErr}
                      </ErrorInfo>
                    )}
                  </FormControl>
                </FormField>
                <FormField style={{ flex: 1 }}>
                  <FormControl sx={{ width: "100%" }}>
                    <Label
                      $hasError={Boolean(
                        changePasswordErrs.newAndConfirmMatchErr
                      )}
                    >
                      Confirm New Password
                    </Label>
                    <TextField
                      error={Boolean(changePasswordErrs.newAndConfirmMatchErr)}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      slotProps={{
                        htmlInput: {
                          // maxLength: passwordMax,
                          minLength: passwordMin,
                        },
                        input: {
                          endAdornment: (
                            <Icon
                              onClick={() => toggleConfirmNewPassword()}
                              src={
                                showConfirmNewPassword ? UnhideIcon : HideIcon
                              }
                            />
                          ),
                        },
                      }}
                      type={showConfirmNewPassword ? "text" : "password"}
                      value={confirmNewPassword}
                      variant="outlined"
                    />
                    {Boolean(changePasswordErrs.newAndConfirmMatchErr) && (
                      <ErrorInfo>
                        {changePasswordErrs.newAndConfirmMatchErr}
                      </ErrorInfo>
                    )}
                  </FormControl>
                </FormField>
              </div>
            </PasswordSection>

            {/* TODO: Tie checkmark/close icons to new password to give users immediate feedback */}
            <PasswordRequirementsContainer>
              <PasswordRequirementsHeader>
                Password Requirements
              </PasswordRequirementsHeader>
              <PasswordRequirementsText>
                <RequirementsIcon
                  src={
                    changePasswordErrs.newPasswordErrs.lengthErr
                      ? CloseIcon
                      : CheckmarkIcon
                  }
                />{" "}
                At least 12 characters in length
              </PasswordRequirementsText>
              <PasswordRequirementsText>
                <RequirementsIcon
                  src={
                    changePasswordErrs.newPasswordErrs.lowerErr
                      ? CloseIcon
                      : CheckmarkIcon
                  }
                />{" "}
                At least 1 lower case character
              </PasswordRequirementsText>
              <PasswordRequirementsText>
                <RequirementsIcon
                  src={
                    changePasswordErrs.newPasswordErrs.upperErr
                      ? CloseIcon
                      : CheckmarkIcon
                  }
                />{" "}
                At least 1 upper case character
              </PasswordRequirementsText>
              <PasswordRequirementsText>
                <RequirementsIcon
                  src={
                    changePasswordErrs.newPasswordErrs.numErr
                      ? CloseIcon
                      : CheckmarkIcon
                  }
                />{" "}
                At least 1 number
              </PasswordRequirementsText>
              <PasswordRequirementsText>
                <RequirementsIcon
                  src={
                    changePasswordErrs.newPasswordErrs.specialErr
                      ? CloseIcon
                      : CheckmarkIcon
                  }
                />{" "}
                At least 1 special character
              </PasswordRequirementsText>
              <PasswordRequirementsText>
                <RequirementsIcon
                  src={
                    changePasswordErrs.newPasswordErrs.recentErr
                      ? CloseIcon
                      : CheckmarkIcon
                  }
                />{" "}
                May not use a password used in last 6 months
              </PasswordRequirementsText>
            </PasswordRequirementsContainer>

            <ButtonGroup>
              <ThemeButton
                disabled={
                  changePasswordErrs.newPasswordErrs.lengthErr ||
                  changePasswordErrs.newPasswordErrs.lowerErr ||
                  changePasswordErrs.newPasswordErrs.upperErr ||
                  changePasswordErrs.newPasswordErrs.numErr ||
                  changePasswordErrs.newPasswordErrs.specialErr ||
                  changePasswordErrs.newPasswordErrs.recentErr ||
                  changePasswordErrs.newAndConfirmMatchErr ||
                  changePasswordErrs.oldAndNewMatchErr
                }
                onClick={() => {
                  changePassword(currPassword, newPassword);
                  setInEditPasswordMode(false);
                }}
                type="button"
              >
                Save Changes
              </ThemeButton>
              <ResetButton
                onClick={(e) => {
                  resetForm(e);
                  setInEditPasswordMode(false);
                }}
                type="button"
              >
                Cancel
              </ResetButton>
            </ButtonGroup>
          </>
        )}
      </InfoSection>
    </Container>
  );
};

export default SecuritySettings;
